import classNames from 'classnames';
import { cardUnits } from '../../../functions';
import { IHandCard } from '../../../Models/CardModels';
import { cardAriaLabel, CardSelection } from '../cardSelection';
import DraggableCard from '../DraggableCard/DraggableCard';

export interface IHandCardsProps {
  handCards: IHandCard[];
  selection: CardSelection;
}

interface IHandLayout {
  cardWidth: number;
  cardHeight: number;
  cardClassName: string;
  rotationPerGroup: number;
  dropPerGroup: number;
  rowTops: number[];
}

interface IHandGroup {
  cards: IHandCard[];
  isSpread: boolean;
}

interface IPositionedCard {
  handCard: IHandCard;
  stackCardIds: string[];
  isTopOfStack: boolean;
  uncoveredWidth: string;
  left: string;
  top: string;
  rotation: number;
}

export const STACK_OFFSET = 10;
const SPREAD_STACK_OFFSET = 32;
const MAX_GROUPS_PER_ROW = 7;
const MAX_GROUP_GAP = 54;
const SIDE_MARGIN = 10;

export const CARD_FACE_CLASS_NAME =
  'block max-w-none rounded-lg outline-1 -outline-offset-1 outline-black/25 shadow-card';

const ONE_ROW: IHandLayout = {
  cardWidth: 84,
  cardHeight: 122,
  cardClassName: `${CARD_FACE_CLASS_NAME} w-[calc(84*var(--card-unit))] h-[calc(122*var(--card-unit))]`,
  rotationPerGroup: 5,
  dropPerGroup: 2.4,
  rowTops: [40],
};

const TWO_ROWS: IHandLayout = {
  cardWidth: 62,
  cardHeight: 90,
  cardClassName: `${CARD_FACE_CLASS_NAME} w-[calc(62*var(--card-unit))] h-[calc(90*var(--card-unit))]`,
  rotationPerGroup: 2.5,
  dropPerGroup: 1.2,
  rowTops: [20, 96],
};

export function sortByValueThenSuit(handCards: IHandCard[]) {
  return [...handCards].sort(
    (a, b) => a.card.value - b.card.value || a.card.suit - b.card.suit
  );
}

function groupByValue(handCards: IHandCard[]) {
  return sortByValueThenSuit(handCards).reduce<IHandCard[][]>(
    (groups, handCard) => {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup[0].card.value === handCard.card.value) {
        lastGroup.push(handCard);
      } else {
        groups.push([handCard]);
      }
      return groups;
    },
    []
  );
}

function stackOffsetOf(group: IHandGroup) {
  return group.isSpread ? SPREAD_STACK_OFFSET : STACK_OFFSET;
}

function fannedOverhang(layout: IHandLayout, groupsFromMiddle: number) {
  const angle = (groupsFromMiddle * layout.rotationPerGroup * Math.PI) / 180;
  const topCornerShift =
    layout.cardHeight * Math.sin(angle) -
    (layout.cardWidth / 2) * (1 - Math.cos(angle));
  return Math.max(0, Math.ceil(topCornerShift));
}

function layoutRow(
  row: IHandGroup[],
  top: number,
  layout: IHandLayout
): IPositionedCard[] {
  const stackedWidth = row.reduce(
    (sum, group) => sum + (group.cards.length - 1) * stackOffsetOf(group),
    0
  );
  const middle = (row.length - 1) / 2;
  const sideMargin = SIDE_MARGIN + fannedOverhang(layout, middle);
  const reservedWidth = 2 * sideMargin + layout.cardWidth + stackedWidth;
  const groupGap = `min(${cardUnits(MAX_GROUP_GAP)}, (100cqw - ${cardUnits(reservedWidth)}) / ${Math.max(row.length - 1, 1)})`;

  let stackedBefore = 0;
  return row.flatMap((group, groupIndex) => {
    const fromMiddle = groupIndex - middle;
    const stackOffset = stackOffsetOf(group);
    const positioned = group.cards.map((handCard, stackIndex) => {
      const fixedOffset =
        stackedBefore +
        stackIndex * stackOffset -
        (stackedWidth + layout.cardWidth) / 2;
      return {
        handCard,
        stackCardIds: group.cards.map((c) => c.card.cardId),
        isTopOfStack: stackIndex === group.cards.length - 1,
        uncoveredWidth: groupGap,
        left: `calc(50% + ${cardUnits(fixedOffset)} + ${fromMiddle} * ${groupGap})`,
        top: cardUnits(top + fromMiddle * fromMiddle * layout.dropPerGroup),
        rotation: fromMiddle * layout.rotationPerGroup,
      };
    });
    stackedBefore += (group.cards.length - 1) * stackOffset;
    return positioned;
  });
}

export default function HandCards({ handCards, selection }: IHandCardsProps) {
  const valueGroups = groupByValue(handCards);
  const {
    selectedIds,
    selectedCards,
    hasSelection,
    isPlayableWith,
    canToggle,
    isRejected,
    tapCard,
    canToggleGroup,
    toggleGroup,
  } = selection;

  const tapStackBadge = (stackCardIds: string[], topCardId: string) => {
    if (canToggleGroup(stackCardIds)) {
      toggleGroup(stackCardIds);
    } else {
      tapCard(topCardId);
    }
  };

  const groups = valueGroups.map(
    (cards): IHandGroup => ({
      cards,
      isSpread: hasSelection && cards.some((c) => canToggle(c.card.cardId)),
    })
  );

  const layout = groups.length > MAX_GROUPS_PER_ROW ? TWO_ROWS : ONE_ROW;
  const backRowLength = Math.ceil(groups.length / layout.rowTops.length);
  const positionedCards = layout.rowTops.flatMap((top, rowIndex) =>
    layoutRow(
      groups.slice(rowIndex * backRowLength, (rowIndex + 1) * backRowLength),
      top,
      layout
    )
  );

  return (
    <section
      aria-label="Your hand"
      className="@container relative w-full h-[calc(212*var(--card-unit))] shrink-0 overflow-x-clip"
    >
      {positionedCards.map(
        ({
          handCard,
          stackCardIds,
          isTopOfStack,
          uncoveredWidth,
          left,
          top,
          rotation,
        }) => {
          const cardId = handCard.card.cardId;
          const isSelected = selectedIds.includes(cardId);
          const isDimmed = hasSelection && !canToggle(cardId);
          return (
            <div
              key={cardId}
              className={classNames(
                'absolute flex origin-bottom transition-[left,top,transform,filter] duration-150',
                { 'brightness-60': isDimmed }
              )}
              style={{ left, top, transform: `rotate(${rotation}deg)` }}
            >
              <DraggableCard
                card={handCard}
                className="relative"
                cardClassName={classNames(layout.cardClassName, {
                  'ring-3 ring-accent-fill': isSelected,
                })}
                rotation={rotation}
                dragGroup={isSelected ? selectedCards : undefined}
                isRejected={isRejected(cardId)}
                ariaLabel={cardAriaLabel(
                  handCard,
                  isSelected,
                  isPlayableWith([cardId])
                )}
                onClick={() => tapCard(cardId)}
              >
                {isTopOfStack && stackCardIds.length > 1 && (
                  <span
                    aria-hidden="true"
                    style={{ left: `calc(${uncoveredWidth} / 2)` }}
                    onClick={(e) => {
                      e.stopPropagation();
                      tapStackBadge(stackCardIds, cardId);
                    }}
                    className="absolute -top-[9px] -translate-x-1/2 flex h-[26px] min-w-[26px] cursor-pointer items-center justify-center rounded-full border-2 border-accent-fill bg-[#151a17] px-[6px] text-[12px] font-bold text-accent-fill"
                  >
                    ×{stackCardIds.length}
                  </span>
                )}
              </DraggableCard>
            </div>
          );
        }
      )}
    </section>
  );
}
